// allDay.ts — reine Helfer für Ganztagstermine (Abwesenheits-Erkennung,
// Graph-Datums-Normalisierung, Mehrtages-Erkennung) und die Slot-Normalisierung
// des Kalender-Neubaus. Bewusst React-frei → testbar und von OutlookKalender /
// TimeGridView / CalendarStateContext gemeinsam genutzt.
//
// SP3 (Wochenansicht-Review, Entscheidung 61): Ganztagstermine laufen über die
// NATIVE RBC-All-Day-Zeile (allDayAccessor in OutlookKalender) statt der
// früheren tageslosen Chip-Leiste (AllDayBar, entfernt) — der frühere
// splitAllDay-Helfer ist damit entfallen.

import moment from "moment";
import { ensureMomentDe } from "../../utils/momentDe";
import type { CalendarGridEvent } from "./types";

// Datums-Labels laufen über moment — Locale defensiv sicherstellen (SP1).
ensureMomentDe();

/**
 * Abwesenheit erkennen (OutlookTermine.tsx:443-446): entweder explizit per
 * Sidecar-Art ODER ein ganztägiges Event mit Abwesenheits-Schlüsselwort im
 * Titel (Alt-Bestand ohne Sidecar). Abwesenheiten gehören IMMER in die
 * RBC-All-Day-Zeile (allDayAccessor), nie ins Stundengitter.
 */
export function istAbwesenheitEvent(e: Pick<CalendarGridEvent, "title" | "allDay" | "meta">): boolean {
    if (e.meta?.terminArt === "abwesenheit") return true;
    return (
        e.allDay && /\b(urlaub|krank|homeoffice|abwesend|ooo|out of office)\b/i.test(e.title || "")
    );
}

// ── Ganztags-Datumsnormalisierung + Mehrtages-Erkennung (SP3, A2) ───────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Graph liefert Ganztags-Zeiten als Mitternacht UTC („2026-07-06T00:00:00.0000000").
 * `toGraphDate` (Z anhängen) ergäbe lokal 02:00 (CEST) — die native
 * RBC-All-Day-Zeile würde die Leiste dann einen Tag zu weit spannen (Aufrunden
 * des 02:00-Endes auf den Folgetag). Für Ganztagstermine zählt NUR der
 * Datumsteil → als LOKALE Mitternacht parsen.
 */
export function graphAllDayDate(dateTime: string): Date {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateTime || "");
    if (!m) return new Date(dateTime);
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Mehrtägiger Ganztagstermin? Graph-Ende ist EXKLUSIV (Mitternacht des
 * Folgetags) → ein eintägiger Ganztagstermin hat exakt 24h Spanne; alles
 * darüber läuft über mehrere Tagesspalten (Verlauf-Optik, Tooltip-Bereich).
 */
export function istMehrtagsAllDay(start: Date, end: Date): boolean {
    return end.getTime() - start.getTime() > MS_PER_DAY;
}

/**
 * Datumsbereich fürs Tooltip mehrtägiger Ganztagstermine: „06.07. – 10.07.".
 * Das exklusive Graph-Ende wird auf den letzten BELEGTEN Tag zurückgerechnet
 * (1 ms vor Mitternacht des Folgetags).
 */
export function allDayBereichLabel(start: Date, end: Date): string {
    const letzterTag = new Date(end.getTime() - 1);
    return `${moment(start).format("DD.MM.")} – ${moment(letzterTag).format("DD.MM.")}`;
}

// ── Slot-Normalisierung (Drag-to-Create, SP3 §3.3/§4.3) ────────────────────

/** Slot-Granularität: feinste sinnvolle Drag-Auflösung (Outlook-typisch). */
export const SLOT_STEP_MIN = 15;
/** 4 × 15 = 60 min/Gruppe → volle Stunde als sichtbare Gruppe. */
export const SLOT_TIMESLOTS = 4;
/** Klick/Null-Dauer → Termin von dieser Mindestdauer (Minuten). */
export const MIN_DAUER_MIN = 30;
/** Monatszellen-Klick → getakteter Default-Termin ab dieser Uhrzeit. */
export const MONTH_DEFAULT_HOUR = 9;

/** RBC-SlotInfo (Teilmenge, die SP3 auswertet). */
export interface RbcSlotInfo {
    start: Date;
    end: Date;
    slots?: Date[];
    action?: "select" | "click" | "doubleClick";
}

/** Normalisierter Slot, der nach außen den SP1-onSelectSlot-Contract bedient. */
export interface NormalizedSlot {
    start: Date;
    end: Date;
    allDay: boolean;
}

/** Liegt das Datum exakt auf Mitternacht (lokale Zeit)? */
const istMitternacht = (d: Date): boolean =>
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;

/**
 * RBC-SlotInfo → {start, end, allDay} normalisieren (Konsistenz-Review A2,
 * SP3 §4.3). Reine Funktion (testbar):
 *
 * - Monatsansicht, EIN Tag: statt allDay legen wir einen GETAKTETEN
 *   Default-Termin (09:00–09:30) an dem Tag an (Entscheidung §11 —
 *   konsistent mit Outlook-Monatsklick).
 * - Monatsansicht, MEHRTÄGIGE Spanne (SP2, Review 2026-07-07): ganztägiger
 *   Termin über die Spanne (Ende EXKLUSIV, Graph-Konvention). Erkennung
 *   PRIMÄR über slots.length > 1 (RBC 1.19.4 liefert je gewähltem Tag einen
 *   Slot); der Dauer-Vergleich ist nur der Fallback ohne slots.
 * - Echte All-Day-Selektion (≥24h-Spanne, auf Mitternacht ausgerichtet) im
 *   Tag/Woche-TimeGrid bzw. in der All-Day-Leiste ⇒ allDay=true.
 * - Klick/Null-Dauer ⇒ Mindestdauer MIN_DAUER_MIN ansetzen.
 *
 * `view` reicht den aktuellen Kalender-View durch (RBC v1.19 setzt slotInfo.allDay
 * nicht zuverlässig — der View-Kontext ist die robustere Quelle).
 */
export function normalizeSlot(
    s: RbcSlotInfo,
    view: "day" | "week" | "month",
): NormalizedSlot {
    const start = new Date(s.start);
    let end = new Date(s.end);
    const dauerMs = end.getTime() - start.getTime();

    // Monatsansicht (RBC 1.19.4, Month.js selectDates): `slots` enthält je
    // gewähltem Tag EIN Date; `end` ist bereits EXKLUSIV (letzter Tag + 1) —
    // auch beim Einzelklick (dann exakt 24h Spanne).
    if (view === "month") {
        const slots = s.slots ?? [];
        const mehrtaegig = slots.length > 0 ? slots.length > 1 : dauerMs > MS_PER_DAY;
        if (mehrtaegig) {
            // Mehrtägige Selektion ⇒ ganztägiger Termin über die Spanne.
            // Enden defensiv auf lokale Mitternacht normalisieren; das Ende
            // bleibt EXKLUSIV (wie die Wochen-All-Day-Zeile / Graph).
            const tagStart = new Date(start);
            tagStart.setHours(0, 0, 0, 0);
            const tagEndeExkl = new Date(end);
            tagEndeExkl.setHours(0, 0, 0, 0);
            if (tagEndeExkl.getTime() < end.getTime()) {
                // Ende lag nicht auf Mitternacht → auf den Folgetag aufrunden.
                tagEndeExkl.setDate(tagEndeExkl.getDate() + 1);
            }
            if (tagEndeExkl.getTime() <= tagStart.getTime()) {
                tagEndeExkl.setTime(tagStart.getTime() + MS_PER_DAY);
            }
            return { start: tagStart, end: tagEndeExkl, allDay: true };
        }
        // Ein-Tages-Klick: getakteter Default-Termin (09:00–09:30) an dem Tag.
        const tagStart = new Date(start);
        tagStart.setHours(MONTH_DEFAULT_HOUR, 0, 0, 0);
        const tagEnd = new Date(tagStart.getTime() + MIN_DAUER_MIN * 60 * 1000);
        return { start: tagStart, end: tagEnd, allDay: false };
    }

    // Echte Ganztags-Selektion: ≥24h-Spanne, beide Enden auf Mitternacht.
    const istAllDay =
        dauerMs >= MS_PER_DAY && istMitternacht(start) && istMitternacht(end);
    if (istAllDay) {
        return { start, end, allDay: true };
    }

    // Klick (Null-Dauer) bzw. zu kurze Auswahl → Mindestdauer anwenden.
    if (dauerMs < MIN_DAUER_MIN * 60 * 1000) {
        end = new Date(start.getTime() + MIN_DAUER_MIN * 60 * 1000);
    }
    return { start, end, allDay: false };
}

/**
 * EXKLUSIVES Ganztags-Ende (NormalizedSlot/Graph-Konvention) → INKLUSIVER
 * letzter Tag für das Ende-Feld des Termin-Formulars: TerminFormWindow rechnet
 * beim Speichern selbst wieder +1 Tag auf das exklusive Graph-Ende. Ohne diese
 * Rückrechnung wäre jeder per Selektion angelegte Ganztagstermin einen Tag zu
 * lang (SP2, Review 2026-07-07).
 */
export function inklusivesAllDayEnde(start: Date, endExklusiv: Date): Date {
    const d = new Date(endExklusiv);
    d.setDate(d.getDate() - 1);
    return d.getTime() < start.getTime() ? new Date(start) : d;
}
